<script setup lang="ts">
	import type { Company } from '@/modules/companies/interfaces/company'
	import GuestsSection from '@/modules/hotels/components/GuestsSection.vue'
	import type { RateForm } from '@/modules/hotels/interfaces/rateForm'
	import type { InputTypes } from '@/modules/hotels/interfaces/rateInputTypes'
	import type { User } from '@/modules/users/interfaces/user'

	defineProps<{
		guests: number
		travelers: number
		form: RateForm
		attemptedToSubmit: boolean
		inputTypes: InputTypes
		users: User[]
		company?: Company
		hotelCode: string
		isCompanyRate: boolean
		provider?: 'amadeus' | 'hotelbeds'
		guestDirty?: Record<string, boolean>
		markGuestDirty?: (key: string) => void
		isGuestDirty?: (key: string) => boolean
		validate: (
			value: any,
			field: string,
			checkInvalidCharacters?: boolean
		) => string | undefined
	}>()

	const emit = defineEmits<{
		submit: []
		'mandatory-change': [value: boolean]
	}>()

	const handleSubmit = () => {
		emit('submit')
	}

	const handleMandatoryChange = (value: boolean) => {
		emit('mandatory-change', value)
	}
</script>

<template>
	<div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
		<div class="bg-gray-50 px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200">
			<div class="flex items-center gap-3">
				<div
					class="w-8 h-8 lg:w-10 lg:h-10 bg-[#021F58]/10 rounded-lg flex items-center justify-center"
				>
					<svg
						class="w-4 h-4 lg:w-5 lg:h-5 text-[#021F58]"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
						></path>
					</svg>
				</div>
				<div>
					<h2
						class="font-inter font-semibold text-base lg:text-lg text-gray-900"
					>
						Información de huéspedes
					</h2>
					<p class="text-xs lg:text-sm text-gray-600">
						Complete los datos de los viajeros
					</p>
				</div>
			</div>
		</div>

		<div class="p-4 lg:p-6">
			<template v-if="form.travelers.length">
				<GuestsSection
					:guests="guests"
					:travelers="travelers"
					:form="form"
					:input-types="inputTypes"
					:validate="validate"
					:attempted-to-submit="attemptedToSubmit"
					:mark-guest-dirty="markGuestDirty"
					:is-guest-dirty="isGuestDirty"
					:users="users"
					:company="company"
					:hotel-code="hotelCode"
					:is-company-rate="isCompanyRate"
					:provider="provider"
					@submit="handleSubmit"
					@mandatory-change="handleMandatoryChange"
				/>
			</template>
			<template v-else>
				<!-- Loading skeleton -->
				<div class="space-y-4 animate-pulse">
					<div class="h-10 lg:h-12 bg-gray-200 rounded-lg"></div>
					<div class="h-10 lg:h-12 bg-gray-200 rounded-lg"></div>
					<div class="h-24 lg:h-32 bg-gray-200 rounded-lg"></div>
				</div>
			</template>
		</div>
	</div>
</template>
